import { useState, useEffect } from 'react';
import countryCodes from '../data/countryCodes.json';

const CACHE_KEY = 'geo_country_code';
const FALLBACK_CODE = 'IN';
const GEO_API_URL = 'https://ipapi.co/json/';
const FETCH_TIMEOUT_MS = 5000;

/**
 * useGeoCountry
 *
 * Detects the user's country based on their IP address.
 * - Starts immediately with the fallback country (IN) — zero delay, no flash.
 * - On first call: fetches ipapi.co/json, caches the result in sessionStorage.
 * - On subsequent calls (same tab session): reads from sessionStorage, no network call.
 * - Gracefully falls back to IN on any error (network failure, unknown code, timeout).
 * - Cleans up via AbortController if the component unmounts before fetch resolves.
 *
 * Returns: { countryCode, setCountryCode }
 *   - countryCode: the matched entry from countryCodes.json
 *   - setCountryCode: exposes the setter so the user can still manually change their selection
 */
function useGeoCountry() {
  const [countryCode, setCountryCode] = useState(
    () => countryCodes.find(c => c.code === FALLBACK_CODE)
  );

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const resolve = async () => {
      try {
        // 1. Check sessionStorage cache first
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) {
          const match = countryCodes.find(c => c.code === cached);
          if (match) {
            setCountryCode(match);
            return;
          }
        }

        // 2. Fetch geo API with a timeout
        const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

        const response = await fetch(GEO_API_URL, { signal });
        clearTimeout(timeoutId);

        if (!response.ok) return; // Silently fall back to IN

        const data = await response.json();
        const detectedCode = data?.country_code;

        if (!detectedCode) return; // Silently fall back to IN

        // 3. Find in our supported country list
        const match = countryCodes.find(c => c.code === detectedCode);
        if (!match) return; // Detected country not in our list — stay on IN

        // 4. Cache in sessionStorage and update state
        sessionStorage.setItem(CACHE_KEY, detectedCode);
        setCountryCode(match);

      } catch (err) {
        // AbortError = component unmounted or timeout — expected, not an error
        if (err.name !== 'AbortError') {
          console.warn('[useGeoCountry] Geo detection failed, using fallback:', err.message);
        }
        // Silently stay on IN — no UI error
      }
    };

    resolve();

    // Cleanup: cancel in-flight fetch if component unmounts
    return () => controller.abort();
  }, []); // Run once on mount only

  return { countryCode, setCountryCode };
}

export default useGeoCountry;
